// ADENDA z52-2 (Diego, 28-oct-2026): la cuenta del componente «Aumento de consumo con IA» (pantallas/cuestionario/AumentoConIA).
// Lo que se acepta con «Usar este aumento» es EXACTAMENTE lo que suma el motor: kwh_mes = la tabla de 12 meses (cada equipo nuevo
// en SUS meses de uso, los más calientes primero: compartido → aumentoPorMesDeCargas, la misma cuenta del servidor; verificación del
// 28-oct: con la curva de las casas 16 % de unos aires nuevos caía en invierno) y, solo para explicar, los equipos.
import { DIAS_MES_CUADRO_CARGAS, aumentoPorMesDeCargas } from '@cotizador/compartido';
import type { AumentoAceptado, AumentoConsumo, Carga, TipoProyecto } from '@cotizador/compartido';
import type { FormaCarga } from './cuestionario.ts';

export const MESES_LARGOS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'] as const;
/** Bloqueo del verificador (29-oct): con la tarifa supuesta (cuestionario «No sé» y sin recibo), por qué el consumo futuro sigue a la
 *  vista y la IA va solo con su botón. */
export const NOTA_SUPUESTA = 'La tarifa todavía no se sabe (se lee del recibo): si resulta GDBT, GDMTO o GDMTH, el aumento con IA no se suma y cuenta el consumo futuro.';
/** Letras mínimas del «¿Por qué?» para calcular solo al salir del campo. */
export const LETRAS_PARA_CALCULAR = 8;

const num = (s: string) => {
  const x = Number(String(s).replace(/,/g, '').trim());
  return Number.isFinite(x) && x >= 0 ? x : 0;
};
const r2 = (x: number) => Math.round(x * 100) / 100;

export function formaDeCargas(cargas: readonly Carga[]): FormaCarga[] {
  return cargas.map((c) => ({ equipo: c.equipo, cantidad: String(c.cantidad), w: String(c.w), horas_dia: String(c.horas_dia), meses_uso: String(c.meses_uso) }));
}

/** Los 12 meses (ENE…DIC) de kWh de más que suma el motor con estos equipos nuevos. */
export function mesesDeAumento(cargas: readonly FormaCarga[]): number[] {
  return aumentoPorMesDeCargas(cargas.map((c) => ({ cantidad: num(c.cantidad), w: num(c.w), horas_dia: num(c.horas_dia), meses_uso: num(c.meses_uso) })));
}

const escalar = (c: FormaCarga, f: number): FormaCarga => ({ ...c, w: String(Math.max(0.01, r2(num(c.w) * f))) });
/** Renglón de kWh parejos todo el año (1 equipo, 24 h, 12 meses). */
function renglonParejo(equipo: string, kwhMes: number): FormaCarga {
  return { equipo, cantidad: '1', w: String(r2((kwhMes * 1000) / (DIAS_MES_CUADRO_CARGAS * 24))), horas_dia: '24', meses_uso: '12' };
}
export const EQUIPO_A_MANO = 'Aumento a mano';
export const EQUIPO_AJUSTE_ANIO = 'Ajuste (todo el año)';

/** Cambia el promedio al mes: todos los equipos se escalan igual (la forma se conserva). */
export function ajustarPromedioAumento(cargas: readonly FormaCarga[], promedio: number): FormaCarga[] {
  const anual = mesesDeAumento(cargas).reduce((a, b) => a + b, 0);
  if (!(anual > 0) || !(promedio > 0)) return [...cargas];
  return cargas.map((c) => escalar(c, (promedio * 12) / anual));
}

/** Cambia UN mes: si ese mes ya trae aumento, todo se escala para que quede como lo escribió; si estaba en 0 (p. ej. enero para
 *  unos aires), se agrega un renglón parejo de todo el año con esos kWh (se ve en los 12 meses: no se esconde). */
export function ajustarMesAumento(cargas: readonly FormaCarga[], mes: number, valor: number): FormaCarga[] {
  if (!(valor > 0)) return [...cargas];
  const meses = mesesDeAumento(cargas);
  if (meses[mes]! > 0) return cargas.map((c) => escalar(c, valor / meses[mes]!));
  return [...cargas, renglonParejo(EQUIPO_AJUSTE_ANIO, valor)];
}

/** Para capturar el aumento A MANO (la IA no respondió y las reglas no reconocen equipos): un renglón parejo que el vendedor ajusta
 *  con el promedio o mes por mes. */
export function cargasAMano(kwhMes = 100): FormaCarga[] {
  return [renglonParejo(EQUIPO_A_MANO, kwhMes)];
}

/** ¿Se calcula solo? Con descripción de 8 letras o más, sin aumento aceptado, sin consumo futuro de antes (ahí lo pide el botón)
 *  y si la descripción cambió desde el último cálculo (al abrir, la de la cotización cuenta como calculada: verificación del 28-oct,
 *  cada vez que se abría una cotización con «va a subir» y sin aumento se gastaba otra llamada a la IA). */
export function debeCalcularAumento(x: { descripcion: string; aceptado: AumentoAceptado | null; ultima: string | null; viejo: number | null }): boolean {
  const d = x.descripcion.trim();
  return d.replace(/\s+/g, '').length >= LETRAS_PARA_CALCULAR && !x.aceptado && x.viejo === null && d !== x.ultima;
}

/** Lo que se guarda en cuestionario.consumo.aumento (null si suma 0: el esquema lo rechazaría). La decisión de la pantalla
 *  (`pantalla`) se le pone al guardar el cuestionario (borradorCuestionario), con los datos de ese momento. */
export function cargasDeAumento(_tipo: TipoProyecto, cargas: readonly FormaCarga[], o: { descripcion: string; fuente: AumentoConsumo['fuente']; motivo: string }): AumentoAceptado | null {
  const kwh_mes = mesesDeAumento(cargas).map(r2);
  if (!(kwh_mes.reduce((a, b) => a + b, 0) > 0)) return null;
  const limpias: Carga[] = cargas
    .map((c) => ({ equipo: c.equipo.trim().slice(0, 60), cantidad: Math.max(1, Math.round(num(c.cantidad))), w: num(c.w), horas_dia: Math.min(24, num(c.horas_dia)), meses_uso: Math.min(12, Math.max(1, Math.round(num(c.meses_uso)))) }))
    .filter((c) => c.equipo.length >= 2 && c.w > 0)
    .slice(0, 40);
  return { kwh_mes, cargas: limpias, descripcion: o.descripcion.trim().slice(0, 500), fuente: o.fuente, motivo: o.motivo.trim() };
}

/** «+10,336 kWh al año (en promedio +861 al mes, +1,723 al bimestre; agosto +1,665)». ADENDA z31: con facturación conocida, también
 *  por periodo del recibo. */
export function resumenAumento(porMes: readonly number[], facturacion: 'mensual' | 'bimestral' | null): { anual: number; promedioMes: number; porPeriodo: { valor: number; al: string } | null; mesAlto: number; alto: number } {
  const anual = porMes.reduce((a, b) => a + b, 0);
  let mesAlto = 0;
  porMes.forEach((x, i) => {
    if (x > porMes[mesAlto]!) mesAlto = i;
  });
  return { anual, promedioMes: anual / 12, porPeriodo: facturacion === 'bimestral' ? { valor: anual / 6, al: 'al bimestre' } : null, mesAlto, alto: porMes[mesAlto] ?? 0 };
}
